import React from 'react';
import clsx from 'clsx';
import { FormattedMessage } from '@99designs/frontend-components/i18n';
import { Role } from '../../consts';
import { Site } from '../../lib/Site';
import NavigationItem from '../NavigationItem/NavigationItem';

const Heading = ({ label }: { label: React.ReactNode }) => (
    <div className="menu__item menu__item--heading menu__item--separated menu__item--spacious-separated">
        {label}
    </div>
);

function MenuSignedOut() {
    return (
        <ul className="menu__item menu__item--action-list">
            <NavigationItem label={__('Browse categories')} url="/categories" icon="notebook-3" />
            <NavigationItem label={__('How it works')} url="/how-it-works" icon="admin-1" />
            <NavigationItem
                label={__('Find a designer')}
                url="/designers/search"
                icon="find-designer-1"
            />
            <NavigationItem label={__('Inspiration')} url="/discover" icon="lightbulb-3" />
            <NavigationItem label="99designs Pro" url="/pro" icon="agency-1" />
        </ul>
    );
}

function MenuDesigner() {
    return (
        <div>
            <Heading label={__('Your work')} />
            <ul className="menu__item menu__item--action-list">
                <NavigationItem
                    label={__('Contests')}
                    url="/contests/your-contests"
                    icon="folder-1"
                />
                <NavigationItem label={__('Projects')} url="/projects/active" icon="folder-1" />
                <NavigationItem
                    label={__('Browse contests')}
                    url="/contests/browse"
                    icon="notebook-3"
                />
                <NavigationItem
                    label={__('Browse projects')}
                    url="/projects/browse"
                    icon="notebook-3"
                />
            </ul>
            <Heading label={__('Community')} />
            <ul className="menu__item menu__item--action-list">
                <NavigationItem
                    label={__('Forum')}
                    url="https://99designs.community"
                    icon="consultation-1"
                    linkAttributes={{ target: '_blank' }}
                />

                <NavigationItem label={__('Your clients')} url="/your-clients" icon="heart-2" />

                <NavigationItem label={__('Inspiration')} url="/discover" icon="lightbulb-3" />

                <NavigationItem
                    label={__('Recent winners')}
                    url="/browse/winners"
                    icon="trophy-1"
                />
            </ul>
        </div>
    );
}

function MenuCustomer() {
    return (
        <ul className="menu__item menu__item--action-list">
            <NavigationItem label={__('Your work')} url="/work" icon="folder-1" />
            <NavigationItem label={__('Designers')} url="/designers/contacts" icon="heart-2" />
            <NavigationItem label={__('Browse categories')} url="/categories" icon="notebook-3" />
            <NavigationItem label={__('Inspiration')} url="/discover" icon="lightbulb-3" />
        </ul>
    );
}

function MenuSwitcher({ role }: { role: Role }) {
    switch (role) {
        case 'contest-holder':
            return <MenuCustomer />;
        case 'designer':
            return <MenuDesigner />;
        case 'logged-out':
        default:
            return <MenuSignedOut />;
    }
}

interface Props {
    role: Role;
    site: Site;
}

export default function OffCanvasPrimary({ role = 'logged-out', site }: Props) {
    const phone = site.get('phone.support');
    const classes = clsx('off-canvas-nav', {
        'off-canvas-nav--signed-in': role !== 'logged-out',
        'off-canvas-nav--signed-out': role === 'logged-out',
    });
    return (
        <div className={classes}>
            <div className="menu">
                <div className="menu__inner">
                    <ul className="menu__item menu__item--action-list">
                        <NavigationItem label={__('Home')} url="/" icon="home-1" />
                    </ul>
                    <MenuSwitcher role={role} />

                    <Heading label={__('Design services')} />
                    <ul className="menu__item menu__item--action-list">
                        <NavigationItem label={__('Design contests')} url="/contests" />
                        <NavigationItem label={__('1-to-1 Projects')} url="/projects" />
                        <NavigationItem label={__('Find a designer')} url="/designers/search" />
                        <NavigationItem label={__('Inspiration')} url="/discover" />
                        <NavigationItem label="99designs Studio" url="/pro/studio" />
                        <NavigationItem label="99designs Pro" url="/pro" />
                    </ul>

                    <Heading label={__('Get a design')} />
                    <ul className="menu__item menu__item--action-list">
                        <NavigationItem label={__('Logo design')} url="/logo-design" />
                        <NavigationItem label={__('Business card')} url="/business-card-design" />
                        <NavigationItem label={__('Web page design')} url="/web-design" />
                        <NavigationItem label={__('Brand guide')} url="/brand-guide" />
                        <NavigationItem label={__('Browse all categories')} url="/categories" />
                    </ul>

                    <Heading label={__('Support')} />
                    <ul className="menu__item menu__item--action-list">
                        {phone && (
                            <NavigationItem
                                label={
                                    <FormattedMessage message={__('{phone}')} values={{ phone }} />
                                }
                                url={`tel:${phone}`}
                                linkAttributes={{ rel: 'nofollow' }}
                                icon="phone-1"
                            />
                        )}
                        <NavigationItem
                            label={__('Help Center')}
                            url="/help"
                            icon="question-mark-1"
                            linkAttributes={{ target: '_blank' }}
                        />
                    </ul>

                    <Heading label={__('Resources')} />
                    <ul className="menu__item menu__item--action-list">
                        <NavigationItem label={__('Pricing')} url="/pricing" />
                        <NavigationItem label={__('Become a designer')} url="/designers" />
                        <NavigationItem label={__('Blog')} url="/blog/" />
                    </ul>
                </div>
            </div>
        </div>
    );
}
